import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchers } from "../lib/api";
import LeagueTabs from "../components/LeagueTabs";
import AdSlot from "../components/AdSlot";

export const StandingsTable = ({ rows, compact = false, testId = "standings-table" }) => (
  <div className="overflow-x-auto border border-border bg-card">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-secondary/60">
        <tr className="text-left overline-eyebrow text-muted-foreground">
          <th className="px-4 py-3">#</th>
          <th className="px-4 py-3">Team</th>
          {["P", "W", "D", "L"].map((h) => <th key={h} className="px-3 py-3 text-right">{h}</th>)}
          {!compact && ["GF", "GA"].map((h) => <th key={h} className="px-3 py-3 text-right">{h}</th>)}
          <th className="px-3 py-3 text-right">GD</th>
          <th className="px-4 py-3 text-right">Pts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t, i) => (
          <tr key={`${t.team}-${i}`} className="border-t border-border hover:bg-secondary/40">
            <td className="px-4 py-3">
              <span className={`inline-flex h-6 w-6 items-center justify-center font-display ${i < 4 ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                {t.rank || i + 1}
              </span>
            </td>
            <td className="px-4 py-3 font-medium">
              <div className="flex items-center gap-2">
                {t.logo && <img src={t.logo} alt="" className="h-5 w-5 object-contain" loading="lazy" />}
                {t.team}
              </div>
            </td>
            {[t.played, t.win, t.draw, t.loss].map((v, k) => <td key={k} className="px-3 py-3 text-right">{v}</td>)}
            {!compact && [t.gf, t.ga].map((v, k) => <td key={k} className="px-3 py-3 text-right">{v}</td>)}
            <td className="px-3 py-3 text-right">{t.gd}</td>
            <td className="px-4 py-3 text-right font-display text-lg text-primary">{t.points}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Standings() {
  const [league, setLeague] = useState("epl");
  const { data, isLoading } = useQuery({ queryKey: ["standings", league], queryFn: () => fetchers.standings(league) });
  const groups = data?.groups?.length ? data.groups : data ? [{ name: data.league, table: data.table }] : [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-6">
        <div className="overline-eyebrow text-primary">League Tables {data?.season && `· ${data.season}`}</div>
        <h1 className="font-display text-5xl uppercase leading-none sm:text-6xl">Standings</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">Live tables from 20 competitions. Top four highlighted.</p>
      </div>

      <AdSlot size="leaderboard" slotId="standings-top" className="mb-8 hidden md:flex" />
      <AdSlot size="large_mobile" slotId="standings-top-m" className="mb-8 md:hidden" />

      <LeagueTabs value={league} onChange={setLeague} />

      <div className="mt-6 grid gap-6 lg:grid-cols-4">
        <div className="space-y-8 lg:col-span-3">
          {isLoading && <div className="py-6 text-sm text-muted-foreground">Loading table…</div>}
          {!isLoading && groups.every((g) => !g.table?.length) && (
            <div className="py-6 text-sm text-muted-foreground">Standings not available for this competition yet.</div>
          )}
          {groups.map((g, i) => (
            <div key={g.name || i}>
              {groups.length > 1 && <h2 className="mb-3 font-display text-2xl uppercase">{g.name}</h2>}
              <StandingsTable rows={g.table || []} testId={i === 0 ? "standings-table" : `standings-table-${i}`} />
            </div>
          ))}
        </div>
        <aside className="space-y-5 lg:sticky lg:top-32 lg:self-start">
          <AdSlot size="rectangle" slotId="standings-side-1" />
          <AdSlot size="half_page" slotId="standings-side-2" className="hidden lg:flex" />
        </aside>
      </div>

      <div className="mt-10">
        <AdSlot size="billboard" slotId="standings-bottom" className="hidden lg:flex" />
        <AdSlot size="responsive" slotId="standings-bottom-m" className="lg:hidden" />
      </div>
    </div>
  );
}
